import Grid from "@components/Grid/Grid";
import SwiperNavigation from "@components/Swiper/SwiperNavigation";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { ProgrammeType } from "@customTypes/ProgrammeType";
import { fetchApi } from "@utilities/fetchApi";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import orderBy from "lodash/orderBy";
import { useEffect, useState } from "react";
import { A11y, Keyboard, Pagination } from "swiper/modules";
import { Swiper as SwiperCarousel, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/a11y";
import "swiper/scss/pagination";

import { Swiper as SwiperProps } from "swiper/types";
import IconCalendar from "../../public/image/svg/calendar.svg";
import IconDuration from "../../public/image/svg/duration.svg";
import IconLocation from "../../public/image/svg/location.svg";
import styles from "./C241_UpcomingProgrammes.module.scss";

export interface UpcommingProgrammesProps {
  programmeType: ProgrammeType;
  label: string;
  title: string;
  description: string;
  BlueVariation?: boolean;
  tags: Array<string>;
}

export const UpcomingProgrammes = (props: {
  content: UpcommingProgrammesProps;
}) => {
  const {
    content: {
      programmeType,
      label,
      title,
      description,
      BlueVariation,
      tags = [],
    },
  } = props;
  const [swiper, setSwiper] = useState<SwiperProps>();
  const [, setSlide] = useState(1);
  const currentPage = Math.round((swiper?.snapIndex || 0) + 1);
  const [upcomingProgrammes, setUpcomingProgrammes] = useState<
    ProgrammeDetails[]
  >([]);
  const device = useDevice();
  const perPage = device === "mobile" ? 1 : device === "tablet" ? 2 : 3;
  const tagIDs = tags;

  const swiperParams = {
    className: clsx(styles.swiper, { ["on-dark"]: BlueVariation }),
    spaceBetween: 40,
    modules: [Pagination, Keyboard, A11y],
    slidesPerView: perPage,
    slidesPerGroup: perPage,
    loop: upcomingProgrammes.length >= 6 && upcomingProgrammes.length % 3 === 0,
    loopAddBlankSlides:
      upcomingProgrammes.length >= 6 && upcomingProgrammes.length % 3 === 0,
    rewind: !(
      upcomingProgrammes.length >= 6 && upcomingProgrammes.length % 3 === 0
    ),
    enabled: upcomingProgrammes.length > perPage,
    pagination: { clickable: true },
    keyboard: { enabled: true, onlyInViewport: true },
    a11y: { enabled: true },
    onSwiper: (swiper) => {
      setSwiper(swiper);
    },
    onSlideChange: () => {
      setSlide((e) => e + 1);
    },
  };

  useEffect(() => {
    const getData = async () => {
      const upcomingProgrammes = await fetchApi("/api/getUpcomingProgrammes", {
        programmeType,
        tagIDs,
      });
      setUpcomingProgrammes(upcomingProgrammes);
    };
    getData();
  }, []);

  return (
    <div
      className={clsx(
        "component",
        styles["upcoming-programmes"],
        BlueVariation && [styles.blue, "blue"]
      )}
    >
      <div className="wrapper">
        <Grid row customClass={styles.row}>
          <Grid column sm={12}>
            <p className={clsx("label", styles.label)}>{label}</p>
          </Grid>
          <Grid column sm={12} md={6}>
            <h2>{title}</h2>
          </Grid>
          <Grid column sm={12} md={6}>
            <p className={clsx("large", styles.large)}>{description}</p>
          </Grid>
        </Grid>
        <div className={styles.programmes}>
          {upcomingProgrammes.length > 0 && (
            <>
              <SwiperCarousel {...swiperParams}>
                {orderBy(upcomingProgrammes, [
                  "programmeImportData.programmeStartDate",
                ]).map((programme, index) => {
                  const {
                    slug,
                    programmeTitle,
                    programmeDescription,
                    isNew,
                    programmeImportData,
                    contentfulMetadata,
                  } = programme;

                  const link = slug.charAt(0) !== "/" ? `/${slug}` : slug;
                  const slideIndex = index + 1;
                  const activeSlide = (swiper?.activeIndex || 0) + 1;
                  const activeSlidesGap = activeSlide + perPage;
                  const isVisible =
                    slideIndex >= activeSlide && slideIndex < activeSlidesGap;
                  return (
                    <SwiperSlide
                      key={slug}
                      // @ts-expect-error no in the typings
                      inert={isVisible ? undefined : "true"}
                      aria-hidden={!isVisible}
                    >
                      <div className={styles.programme}>
                        {isNew ? <div className={styles.new}>NEW</div> : null}
                        <h3>
                          <a href={link}>{programmeTitle}</a>
                        </h3>
                        <p>{programmeDescription}</p>
                        <div className={styles.bottom}>
                          <ul>
                            {programmeImportData &&
                              programmeImportData[0]
                                ?.programmeInclusiveDates && (
                                <li>
                                  <i className="icon-calendar">
                                    <IconCalendar />
                                  </i>
                                  {
                                    programmeImportData[0]
                                      .programmeInclusiveDates
                                  }
                                </li>
                              )}
                            <li>
                              <i className="icon-duration">
                                <IconDuration />
                              </i>
                              {programmeImportData
                                ? programmeImportData[0]?.programmeDuration
                                : ""}
                            </li>
                            <li>
                              <i className="icon-location">
                                <IconLocation />
                              </i>
                              {programmeImportData
                                ? programmeImportData[0]?.programmeLocation
                                : ""}
                            </li>
                          </ul>
                          {!!contentfulMetadata?.tags.length && (
                            <p className="small">
                              <strong>Tags: </strong>
                              {contentfulMetadata?.tags
                                .map((tag) => {
                                  return tag.name;
                                })
                                .join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </SwiperCarousel>
              {upcomingProgrammes.length > perPage && (
                <SwiperNavigation
                  className={styles.next}
                  swiper={swiper}
                  direction="next"
                  perPage={perPage}
                  variation={device === "desktop-large" ? "fraction" : "arrow"}
                  onDark={BlueVariation}
                  currentPage={currentPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingProgrammes;
